import axios, { AxiosRequestConfig } from 'axios';
import { useErrorStore } from '../store/errorStore';
import { useUserStore } from '../store/userStore';

// Ответ обычного запроса ждём ограниченное время. Раньше таймаута не было
// вовсе, и залипший бэкенд оставлял страницу с вечным лоадером — снаружи это
// выглядит как «сайт перестал отвечать». Долгим операциям (синхронизация
// склада) таймаут отключается точечно через `timeout: 0` в самом запросе.
export const DEFAULT_TIMEOUT = 20000;

// Сессия кончилась: App слушает это событие и уводит на /auth средствами
// роутера. Раньше здесь стоял window.location.href — страница перезагружалась
// целиком, пользователь просто оказывался на форме входа без объяснений.
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: DEFAULT_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Запросы, на которые не надо реагировать разлогином: сам refresh и вход.
const AUTH_ENDPOINTS = ['/api/refresh-token', '/api/login'];

function isAuthEndpoint(url?: string): boolean {
    if (!url) return false;
    return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function clearSession() {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    // Чистим и стор, иначе пользователь остаётся «залогиненным» без токена:
    // шапка показывает имя, а каждый защищённый запрос падает с 401.
    useUserStore.getState().clearUser();
}

function notifySessionExpired() {
    clearSession();
    window.dispatchEvent(
        new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: { from: window.location.pathname + window.location.search },
        })
    );
}

// Общий промис обновления токена. Если страница разом отправила несколько
// запросов, все они получат 401 одновременно — без этого каждый запускал бы
// свой refresh, и часть из них ловила бы уже использованный токен.
let refreshPromise: Promise<string> | null = null;

async function refreshToken(): Promise<string> {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken) {
        throw new Error('Рефреш-токен отсутствует');
    }

    const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/refresh-token`,
        { refresh_token: storedRefreshToken },
        { timeout: DEFAULT_TIMEOUT }
    );
    const { token, refresh_token: newRefreshToken } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('refresh_token', newRefreshToken);

    return token;
}

function refreshTokenOnce(): Promise<string> {
    if (!refreshPromise) {
        refreshPromise = refreshToken().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
        const setError = useErrorStore.getState().setError;

        if (
            error.response &&
            error.response.status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !isAuthEndpoint(originalRequest.url)
        ) {
            // Токена нет вовсе: это либо гость, либо параллельный запрос уже
            // после разлогина. Ни обновлять нечего, ни выбрасывать некого —
            // отдаём ошибку молча, чтобы не гонять человека на /auth по кругу.
            if (!localStorage.getItem('token')) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                const newToken = await refreshTokenOnce();
                originalRequest.headers = {
                    ...(originalRequest.headers || {}),
                    Authorization: `Bearer ${newToken}`,
                };
                return apiClient(originalRequest); // Повторяем запрос с новым токеном
            } catch (refreshError) {
                notifySessionExpired();
                return Promise.reject(refreshError);
            }
        }

        // Обработка других ошибок
        if (error.response) {
            const message = error.response.data?.error || 'Произошла ошибка на сервере';
            setError(message);
        } else if (error.code === 'ECONNABORTED') {
            // Сработал таймаут: честно говорим, что ответа не дождались.
            setError('Сервер долго не отвечает. Попробуйте ещё раз.');
        } else if (error.request) {
            setError('Сервер недоступен. Попробуйте позже.');
        } else {
            setError('Ошибка запроса: ' + error.message);
        }

        return Promise.reject(error);
    }
);

export default apiClient;
